import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HardDrive, Trash2, Calendar, Image, AlertTriangle, X, Eye, Download, ChevronLeft, ChevronRight, Lock, RotateCcw, Clock, Search, CheckSquare, Square, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI, BACKEND_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';


const StorageDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [storageData, setStorageData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateDetails, setSelectedDateDetails] = useState(null);
  const [loadingDateDetails, setLoadingDateDetails] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Password protection
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Recycle bin
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [recycleBinData, setRecycleBinData] = useState(null);
  const [selectedRecycleItems, setSelectedRecycleItems] = useState([]);
  const [recovering, setRecovering] = useState(false);
  const [emptying, setEmptying] = useState(false);
  const [recycleBinSearch, setRecycleBinSearch] = useState('');
  
  const navigate = useNavigate();
  const { user, userType, loading: authLoading } = useAuth();

  // Auth check only
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await adminAPI.verifyStoragePassword(password);
      setIsAuthenticated(true);
      fetchStorageData();
      toast.success('Access granted');
    } catch (error) {
      toast.error('Invalid password');
      setPassword('');
    } finally {
      setVerifying(false);
    }
  };

  const fetchStorageData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getStorageSummary();
      setStorageData(response.data);
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load storage data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchStorageData(true);
  };

  const fetchRecycleBin = async () => {
    try {
      // First cleanup expired items
      await adminAPI.cleanupRecycleBin();
      // Then fetch
      const response = await adminAPI.getRecycleBin();
      setRecycleBinData(response.data);
    } catch (error) {
      toast.error('Failed to load recycle bin');
    }
  };

  // Get available dates from storage data
  const getAvailableDates = () => {
    return storageData?.by_date?.map(d => d.date) || [];
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dayDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        date: dayDate,
        hasData: getAvailableDates().includes(dayDate)
      });
    }
    
    return days;
  };

  const handleDateSelect = async (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedPhotos([]);
    setShowDatePicker(false);
    setSelectedDateDetails(null);
    
    // Fetch date details on demand
    if (dateStr) {
      setLoadingDateDetails(true);
      try {
        const response = await adminAPI.getStorageDateDetails(dateStr);
        setSelectedDateDetails(response.data);
      } catch (error) {
        toast.error('Failed to load date details');
        console.error('Date details error:', error);
      } finally {
        setLoadingDateDetails(false);
      }
    }
  };

  const handleDeletePhotos = async () => {
    if (!selectedDate || selectedPhotos.length === 0) {
      toast.error('Please select photos to delete');
      return;
    }
    
    setDeleting(true);
    try {
      await adminAPI.deletePhotos(selectedDate, null, selectedPhotos);
      toast.success(`${selectedPhotos.length} photo(s) moved to recycle bin`);
      setSelectedPhotos([]);
      setShowDeleteConfirm(false);
      fetchStorageData();
    } catch (error) {
      toast.error('Failed to delete photos');
    } finally {
      setDeleting(false);
    }
  };

  const handleRecoverPhotos = async () => {
    if (selectedRecycleItems.length === 0) {
      toast.error('Please select items to recover');
      return;
    }
    
    setRecovering(true);
    try {
      await adminAPI.recoverFromRecycleBin(selectedRecycleItems);
      toast.success(`${selectedRecycleItems.length} photo(s) recovered`);
      setSelectedRecycleItems([]);
      fetchRecycleBin();
      fetchStorageData();
    } catch (error) {
      toast.error('Failed to recover photos');
    } finally {
      setRecovering(false);
    }
  };

  const handleEmptyRecycleBin = async () => {
    if (!window.confirm('Are you sure you want to permanently delete all items in the recycle bin? This cannot be undone.')) {
      return;
    }
    
    setEmptying(true);
    try {
      await adminAPI.emptyRecycleBin();
      toast.success('Recycle bin emptied');
      setRecycleBinData({ items: [], total: 0 });
      setSelectedRecycleItems([]);
    } catch (error) {
      toast.error('Failed to empty recycle bin');
    } finally {
      setEmptying(false);
    }
  };

  const togglePhotoSelection = (photoUrl) => {
    setSelectedPhotos(prev => 
      prev.includes(photoUrl) 
        ? prev.filter(p => p !== photoUrl)
        : [...prev, photoUrl]
    );
  };

  const toggleRecycleItemSelection = (itemId) => {
    setSelectedRecycleItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Filter recycle bin items based on search
  const getFilteredRecycleBinItems = () => {
    if (!recycleBinData?.items) return [];
    if (!recycleBinSearch.trim()) return recycleBinData.items;
    
    const searchLower = recycleBinSearch.toLowerCase();
    return recycleBinData.items.filter(item => 
      item.park_name?.toLowerCase().includes(searchLower) ||
      item.park_id?.toLowerCase().includes(searchLower) ||
      item.photo_type?.toLowerCase().includes(searchLower) ||
      item.date?.includes(searchLower)
    );
  };

  // Select all filtered recycle bin items
  const selectAllRecycleBinItems = () => {
    const filteredItems = getFilteredRecycleBinItems();
    const allIds = filteredItems.map(item => item.id);
    setSelectedRecycleItems(allIds);
  };

  // Deselect all recycle bin items
  const deselectAllRecycleBinItems = () => {
    setSelectedRecycleItems([]);
  };

  // Check if all filtered items are selected
  const areAllRecycleBinItemsSelected = () => {
    const filteredItems = getFilteredRecycleBinItems();
    if (filteredItems.length === 0) return false;
    return filteredItems.every(item => selectedRecycleItems.includes(item.id));
  };

  const selectAllPhotos = (inspections) => {
    const allPhotos = [];
    inspections.forEach(insp => {
      if (insp.selfie_photo) allPhotos.push(insp.selfie_photo);
      (insp.worker_photos || []).forEach(p => allPhotos.push(p));
      (insp.cleanliness_photos || []).forEach(p => allPhotos.push(p));
    });
    setSelectedPhotos(allPhotos);
  };

  const selectColumnPhotos = (inspections, columnType) => {
    const columnPhotos = [];
    inspections.forEach(insp => {
      const workerPhotos = insp.worker_photos || insp.additional_photos || [];
      const cleanPhotos = insp.cleanliness_photos || [];
      
      switch(columnType) {
        case 'selfie':
          if (insp.selfie_photo) columnPhotos.push(insp.selfie_photo);
          break;
        case 'worker1':
          if (workerPhotos[0]) columnPhotos.push(workerPhotos[0]);
          break;
        case 'worker2':
          if (workerPhotos[1]) columnPhotos.push(workerPhotos[1]);
          break;
        case 'worker3':
          if (workerPhotos[2]) columnPhotos.push(workerPhotos[2]);
          break;
        case 'clean1':
          if (cleanPhotos[0]) columnPhotos.push(cleanPhotos[0]);
          break;
        case 'clean2':
          if (cleanPhotos[1]) columnPhotos.push(cleanPhotos[1]);
          break;
        case 'clean3':
          if (cleanPhotos[2]) columnPhotos.push(cleanPhotos[2]);
          break;
        default:
          break;
      }
    });
    
    const allSelected = columnPhotos.every(p => selectedPhotos.includes(p));
    if (allSelected && columnPhotos.length > 0) {
      setSelectedPhotos(prev => prev.filter(p => !columnPhotos.includes(p)));
    } else {
      setSelectedPhotos(prev => [...new Set([...prev, ...columnPhotos])]);
    }
  };

  const isColumnAllSelected = (inspections, columnType) => {
    const columnPhotos = [];
    inspections.forEach(insp => {
      const workerPhotos = insp.worker_photos || insp.additional_photos || [];
      const cleanPhotos = insp.cleanliness_photos || [];
      
      switch(columnType) {
        case 'selfie':
          if (insp.selfie_photo) columnPhotos.push(insp.selfie_photo);
          break;
        case 'worker1':
          if (workerPhotos[0]) columnPhotos.push(workerPhotos[0]);
          break;
        case 'worker2':
          if (workerPhotos[1]) columnPhotos.push(workerPhotos[1]);
          break;
        case 'worker3':
          if (workerPhotos[2]) columnPhotos.push(workerPhotos[2]);
          break;
        case 'clean1':
          if (cleanPhotos[0]) columnPhotos.push(cleanPhotos[0]);
          break;
        case 'clean2':
          if (cleanPhotos[1]) columnPhotos.push(cleanPhotos[1]);
          break;
        case 'clean3':
          if (cleanPhotos[2]) columnPhotos.push(cleanPhotos[2]);
          break;
        default:
          break;
      }
    });
    return columnPhotos.length > 0 && columnPhotos.every(p => selectedPhotos.includes(p));
  };

  const deselectAllPhotos = () => {
    setSelectedPhotos([]);
  };

  const isPhotoSelected = (photoUrl) => selectedPhotos.includes(photoUrl);

  const renderPhotoCell = (photoUrl, label) => {
    if (!photoUrl) return <span className="text-slate-300">-</span>;
    
    const isBase64 = photoUrl?.startsWith('data:');
    
    return (
      <div className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={isPhotoSelected(photoUrl)}
          onChange={() => togglePhotoSelection(photoUrl)}
          className="w-4 h-4 rounded border-slate-300"
        />
        <button
          onClick={() => setViewingPhoto({ url: photoUrl, label })}
          className="p-1 hover:bg-blue-100 rounded text-blue-600"
          title="View photo"
        >
          <Eye className="w-4 h-4" />
        </button>
        {isBase64 && <span className="text-xs text-orange-500">(B64)</span>}
      </div>
    );
  };

  // Password Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-6">
            <div className="flex items-center gap-3">
              <Lock className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Storage Dashboard</h1>
                <p className="text-purple-200 text-sm">Protected Area</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Enter Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Storage password"
                className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg focus:border-purple-600 focus:ring-0"
                data-testid="storage-password-input"
                autoFocus
              />
            </div>
            
            <button
              type="submit"
              disabled={!password || verifying}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center justify-center gap-2"
              data-testid="storage-password-submit"
            >
              {verifying ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Access Storage
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="w-full h-10 text-slate-600 hover:text-slate-800 font-semibold"
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const selectedDateSummary = storageData?.by_date?.find(d => d.date === selectedDate);
  const monthName = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/dashboard')} 
              className="p-2 hover:bg-white/20 rounded-lg"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Storage Dashboard</h1>
              <p className="text-purple-200 text-sm">Manage photo storage</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="h-10 px-4 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded-lg font-semibold flex items-center gap-2"
              data-testid="refresh-storage-btn"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            
            {/* Recycle Bin Button */}
            <button
              onClick={() => {
                setShowRecycleBin(true);
                fetchRecycleBin();
              }}
              className="h-10 px-4 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2"
              data-testid="open-recycle-bin"
            >
              <Trash2 className="w-5 h-5" />
              Recycle Bin
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 max-w-7xl mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{storageData?.total_size_mb} MB</div>
                <div className="text-sm text-slate-500">Total Storage</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Image className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{storageData?.total_photos}</div>
                <div className="text-sm text-slate-500">Total Photos</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{storageData?.by_date?.length || 0}</div>
                <div className="text-sm text-slate-500">Days with Data</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <button 
              onClick={() => setShowDatePicker(true)}
              className="w-full h-full flex items-center justify-center gap-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              data-testid="open-date-picker"
            >
              <Calendar className="w-6 h-6" />
              <span className="font-bold">Select Date</span>
            </button>
          </div>
        </div>

        {/* Date Selection Info */}
        {selectedDate && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-purple-800">Selected Date: {selectedDate}</h3>
                <p className="text-sm text-purple-600">
                  {selectedDateDetails?.inspection_count || selectedDateSummary?.inspection_count || 0} inspections • 
                  {selectedDateDetails?.total_photos || selectedDateSummary?.photo_count || 0} photos • 
                  {selectedDateSummary?.estimated_size_kb ? (selectedDateSummary.estimated_size_kb / 1024).toFixed(2) : 0} MB
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedPhotos.length > 0 && (
                  <span className="bg-purple-200 text-purple-800 px-3 py-1 rounded-full text-sm font-bold">
                    {selectedPhotos.length} selected
                  </span>
                )}
                <button
                  onClick={() => { setSelectedDate(null); setSelectedDateDetails(null); }}
                  className="p-2 hover:bg-purple-100 rounded-lg text-purple-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Date List */}
        {!selectedDate && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="p-4 border-b bg-slate-50">
              <h2 className="font-bold text-lg">Available Dates</h2>
              <p className="text-sm text-slate-500">Click on a date to view and manage photos</p>
            </div>
            
            <div className="divide-y max-h-64 overflow-y-auto">
              {storageData?.by_date?.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No photo data found</div>
              ) : (
                storageData?.by_date?.map((dateData) => (
                  <div 
                    key={dateData.date}
                    className="p-4 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between"
                    onClick={() => handleDateSelect(dateData.date)}
                    data-testid={`date-row-${dateData.date}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <Calendar className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <div className="font-semibold">{dateData.date}</div>
                        <div className="text-sm text-slate-500">
                          {dateData.inspection_count || 0} inspections
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-slate-700">{dateData.photo_count} photos</div>
                      <div className="text-sm text-slate-500">
                        {(dateData.estimated_size_kb / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Photo Grid/Table for Selected Date */}
        {selectedDate && (loadingDateDetails ? (
          <div className="bg-white rounded-xl shadow-sm p-12 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-600">Loading inspection details...</p>
          </div>
        ) : selectedDateDetails ? (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-bold text-lg">Photos for {selectedDate}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => selectAllPhotos(selectedDateDetails.inspections || [])}
                  className="h-9 px-3 bg-slate-200 hover:bg-slate-300 rounded-lg text-sm font-semibold"
                  data-testid="select-all-photos"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllPhotos}
                  className="h-9 px-3 bg-slate-200 hover:bg-slate-300 rounded-lg text-sm font-semibold"
                  data-testid="deselect-all-photos"
                >
                  Deselect All
                </button>
                {selectedPhotos.length > 0 && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
                    data-testid="delete-selected-photos"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete ({selectedPhotos.length})
                  </button>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-bold">Park</th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Selfie</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'selfie')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'selfie')}
                          className="w-4 h-4 rounded border-slate-300"
                          title="Select all Selfie photos"
                        />
                      </div>
                    </th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Worker 1</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'worker1')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'worker1')}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </div>
                    </th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Worker 2</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'worker2')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'worker2')}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </div>
                    </th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Worker 3</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'worker3')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'worker3')}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </div>
                    </th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Clean 1</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'clean1')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'clean1')}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </div>
                    </th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Clean 2</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'clean2')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'clean2')}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </div>
                    </th>
                    <th className="text-center p-2 font-bold">
                      <div className="flex flex-col items-center gap-1">
                        <span>Clean 3</span>
                        <input
                          type="checkbox"
                          checked={isColumnAllSelected(selectedDateDetails.inspections || [], 'clean3')}
                          onChange={() => selectColumnPhotos(selectedDateDetails.inspections || [], 'clean3')}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </div>
                    </th>
                    <th className="text-center p-3 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedDateDetails.inspections || []).map((insp, idx) => {
                    const workerPhotos = insp.worker_photos || insp.additional_photos || [];
                    const cleanPhotos = insp.cleanliness_photos || [];
                    const totalPhotos = (insp.selfie_photo ? 1 : 0) + workerPhotos.length + cleanPhotos.length;
                    
                    return (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-semibold">{insp.park_name}</div>
                          <div className="text-xs text-slate-500">{insp.park_id}</div>
                        </td>
                        <td className="p-3 text-center">{renderPhotoCell(insp.selfie_photo, 'Selfie')}</td>
                        <td className="p-3 text-center">{renderPhotoCell(workerPhotos[0], 'Worker 1')}</td>
                        <td className="p-3 text-center">{renderPhotoCell(workerPhotos[1], 'Worker 2')}</td>
                        <td className="p-3 text-center">{renderPhotoCell(workerPhotos[2], 'Worker 3')}</td>
                        <td className="p-3 text-center">{renderPhotoCell(cleanPhotos[0], 'Cleanliness 1')}</td>
                        <td className="p-3 text-center">{renderPhotoCell(cleanPhotos[1], 'Cleanliness 2')}</td>
                        <td className="p-3 text-center">{renderPhotoCell(cleanPhotos[2], 'Cleanliness 3')}</td>
                        <td className="p-3 text-center font-bold text-slate-700">{totalPhotos}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null)}
      </div>

      {/* Recycle Bin Modal */}
      {showRecycleBin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="bg-slate-800 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Trash2 className="w-6 h-6" />
                  Recycle Bin
                </h3>
                <p className="text-sm text-slate-300">
                  Items are automatically deleted after 48 hours
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedRecycleItems.length > 0 && (
                  <button
                    onClick={handleRecoverPhotos}
                    disabled={recovering}
                    className="h-10 px-4 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-lg font-semibold flex items-center gap-2"
                    data-testid="recover-selected"
                  >
                    {recovering ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    Recover ({selectedRecycleItems.length})
                  </button>
                )}
                {recycleBinData?.items?.length > 0 && (
                  <button
                    onClick={handleEmptyRecycleBin}
                    disabled={emptying}
                    className="h-10 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg font-semibold flex items-center gap-2"
                    data-testid="empty-recycle-bin"
                  >
                    {emptying ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Empty Bin
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowRecycleBin(false);
                    setSelectedRecycleItems([]);
                    setRecycleBinSearch('');
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {!recycleBinData ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto" />
                  <p className="mt-2 text-slate-500">Loading...</p>
                </div>
              ) : recycleBinData.items?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Trash2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="font-semibold">Recycle bin is empty</p>
                </div>
              ) : (
                <>
                  {/* Search and Select All Controls */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg border">
                    {/* Search Input */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search by park name, ID, type, or date..."
                        value={recycleBinSearch}
                        onChange={(e) => setRecycleBinSearch(e.target.value)}
                        className="w-full h-10 pl-10 pr-4 border border-slate-300 rounded-lg focus:border-slate-500 focus:ring-0"
                        data-testid="recycle-bin-search"
                      />
                    </div>
                    
                    {/* Select/Deselect All Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={areAllRecycleBinItemsSelected() ? deselectAllRecycleBinItems : selectAllRecycleBinItems}
                        className={`h-10 px-4 rounded-lg font-semibold flex items-center gap-2 ${
                          areAllRecycleBinItemsSelected() 
                            ? 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                        data-testid="recycle-bin-select-all"
                      >
                        {areAllRecycleBinItemsSelected() ? (
                          <>
                            <Square className="w-4 h-4" />
                            Deselect All
                          </>
                        ) : (
                          <>
                            <CheckSquare className="w-4 h-4" />
                            Select All ({getFilteredRecycleBinItems().length})
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Filtered Results Info */}
                  {recycleBinSearch && (
                    <div className="text-sm text-slate-500 mb-3">
                      Showing {getFilteredRecycleBinItems().length} of {recycleBinData.items.length} items
                    </div>
                  )}
                  
                  {/* Items List */}
                  <div className="space-y-3">
                    {getFilteredRecycleBinItems().length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Search className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No items match your search</p>
                      </div>
                    ) : (
                      getFilteredRecycleBinItems().map((item) => (
                        <div 
                          key={item.id}
                          className={`border rounded-lg p-3 flex items-center gap-4 ${
                            selectedRecycleItems.includes(item.id) ? 'border-green-500 bg-green-50' : 'border-slate-200'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRecycleItems.includes(item.id)}
                            onChange={() => toggleRecycleItemSelection(item.id)}
                            className="w-5 h-5 rounded border-slate-300"
                          />
                          
                          {/* Photo thumbnail */}
                          <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={item.photo_url} 
                              alt={item.photo_type}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          <div className="flex-1">
                            <div className="font-semibold">{item.park_name}</div>
                            <div className="text-sm text-slate-500">
                              Type: {item.photo_type} • Date: {item.date}
                            </div>
                            <div className="text-xs text-slate-400">
                              Deleted: {item.deleted_at?.split('T')[0]}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-orange-600">
                              <Clock className="w-4 h-4" />
                              <span className="font-semibold">{item.hours_remaining}h left</span>
                            </div>
                            <button
                              onClick={() => setViewingPhoto({ url: item.photo_url, label: `${item.park_name} - ${item.photo_type}` })}
                              className="text-blue-600 hover:text-blue-700 text-sm font-semibold"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-purple-600 text-white p-4 flex items-center justify-between rounded-t-xl">
              <h3 className="font-bold">Select Date</h3>
              <button onClick={() => setShowDatePicker(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-bold">{monthName}</span>
                <button
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-xs font-bold text-slate-500 p-2">{day}</div>
                ))}
                {getDaysInMonth(calendarDate).map((dayInfo, idx) => (
                  <div key={idx}>
                    {dayInfo ? (
                      <button
                        onClick={() => dayInfo.hasData && handleDateSelect(dayInfo.date)}
                        disabled={!dayInfo.hasData}
                        className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                          dayInfo.hasData 
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer' 
                            : 'text-slate-300 cursor-not-allowed'
                        } ${selectedDate === dayInfo.date ? 'ring-2 ring-purple-600' : ''}`}
                      >
                        {dayInfo.day}
                      </button>
                    ) : (
                      <div className="w-10 h-10" />
                    )}
                  </div>
                ))}
              </div>

              <p className="text-sm text-slate-500 text-center">
                <span className="inline-block w-3 h-3 bg-purple-100 rounded mr-1" /> Dates with photos
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {viewingPhoto && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-slate-800 text-white p-4 flex items-center justify-between">
              <h3 className="font-bold">{viewingPhoto.label}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={viewingPhoto.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button onClick={() => setViewingPhoto(null)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center bg-slate-100 max-h-[70vh] overflow-auto">
              <img 
                src={viewingPhoto.url} 
                alt={viewingPhoto.label} 
                className="max-w-full max-h-[60vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-orange-500 text-white p-4 flex items-center gap-3 rounded-t-xl">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="font-bold">Move to Recycle Bin</h3>
            </div>
            
            <div className="p-4">
              <p className="text-slate-700 mb-4">
                Move <strong>{selectedPhotos.length}</strong> photo(s) to recycle bin?
              </p>
              <p className="text-sm text-slate-500 mb-4">
                Photos will be automatically deleted after 48 hours. You can recover them before then.
              </p>
            </div>
            
            <div className="border-t p-4 flex gap-3">
              <button
                onClick={handleDeletePhotos}
                disabled={deleting}
                className="flex-1 h-12 bg-orange-500 text-white rounded-lg font-bold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Moving...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Move to Recycle Bin
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorageDashboard;
